import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { listHospitals, Hospital } from "../../services/superAdmin.service";
import {
  getBrandingList,
  HospitalMdtBranding,
} from "../../services/hospitalMdtBranding.service";
import MdtStructureEditor from "./MdtStructureEditor";
import MDTTemplateEditor from "./MDTTemplateEditor";
import { LayoutList, Building2, Edit, Plus, Search, FileText, ChevronRight, Filter, ChevronLeft } from "lucide-react";
import CustomSelect from "../../components/common/CustomSelect";
import { paginationPageWindow } from "../../utils/pagination";

const BRANDING_OPTIONS = [
  { label: "All Branding", value: "" },
  { label: "Configured", value: "configured" },
  { label: "Not Set Up", value: "not-configured" },
];

const pageSize = 10;

type Tab = "structure" | "branding";

interface BrandingRow {
  hospital: Hospital;
  branding: HospitalMdtBranding | null;
}

const MDTTemplateList = () => {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "branding" ? "branding" : "structure";

  const setTab = (next: Tab) => setParams({ tab: next });

  const [rows, setRows] = useState<BrandingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editingHospitalId, setEditingHospitalId] = useState<string | null>(null);

  // Filters
  const [brandingFilter, setBrandingFilter] = useState("");
  const [domainFilter, setDomainFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [hospitalsRes, brandingRes] = await Promise.all([
        listHospitals({ limit: 1000 }),
        getBrandingList({ limit: 1000 }),
      ]);

      const byHospital: Record<string, HospitalMdtBranding> = {};
      brandingRes.branding.forEach((b) => {
        byHospital[b.hospitalId] = b;
      });

      setRows(hospitalsRes.hospitals.map((h) => ({ hospital: h, branding: byHospital[h.id] || null })));
    } catch {
      toast.error("Failed to load hospital branding");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "branding") fetchData();
  }, [tab, fetchData]);

  const filteredRows = useMemo(() => {
    let result = rows;

    if (search.trim()) {
      const term = search.trim().toLowerCase();
      result = result.filter(
        (r) =>
          r.hospital.hospitalName.toLowerCase().includes(term) ||
          (r.hospital.domainName || "").toLowerCase().includes(term) ||
          (r.hospital.hospitalCode || "").toLowerCase().includes(term)
      );
    }

    if (brandingFilter === "configured") {
      result = result.filter((r) => r.branding !== null && r.hospital.status === "ACTIVE");
    } else if (brandingFilter === "not-configured") {
      result = result.filter((r) => r.branding === null && r.hospital.status === "ACTIVE");
    }

    if (domainFilter.trim()) {
      const term = domainFilter.trim().toLowerCase();
      result = result.filter((r) => (r.hospital.domainName || "").toLowerCase().includes(term));
    }

    return result;
  }, [rows, search, brandingFilter, domainFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const hasActiveFilters = !!search.trim() || !!(brandingFilter || domainFilter);

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex items-center gap-6 border-b border-gray-200">
        <TabButton active={tab === "structure"} onClick={() => setTab("structure")}>
          <LayoutList size={16} />
          Report structure
        </TabButton>
        <TabButton active={tab === "branding"} onClick={() => setTab("branding")}>
          <Building2 size={16} />
          Hospital branding
        </TabButton>
      </div>

      {tab === "structure" ? (
        <MdtStructureEditor />
      ) : editingHospitalId ? (
        <MDTTemplateEditor
          hospitalId={editingHospitalId}
          onBack={() => setEditingHospitalId(null)}
        />
      ) : (
        <div className="space-y-6">
          {/* Search & Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 space-y-4">
            <form onSubmit={(e) => { e.preventDefault(); setPage(1); }} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search by hospital name, domain, or code..."
                  className="w-full h-10 pl-10 pr-4 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <CustomSelect
                value={brandingFilter}
                onChange={(val) => { setBrandingFilter(val); setPage(1); }}
                options={BRANDING_OPTIONS}
                variant="filter"
                placeholder="All Branding"
                className="h-10"
              />
              <button
                type="button"
                onClick={() => setShowFilters((prev) => !prev)}
                className="flex items-center justify-center gap-2 px-4 h-10 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white shadow-sm transition-colors shrink-0"
              >
                <Filter size={16} />
                <span>Filters</span>
              </button>
            </form>

            {showFilters && (
              <form onSubmit={(e) => { e.preventDefault(); setPage(1); }} className="pt-4 border-t border-gray-100 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Domain Name</label>
                    <input
                      type="text"
                      value={domainFilter}
                      onChange={(e) => { setDomainFilter(e.target.value); setPage(1); }}
                      placeholder="Filter by domain..."
                      className="w-full h-10 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-700 placeholder-gray-400"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => { setDomainFilter(""); setPage(1); }}
                    className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    Clear Filters
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Apply Filters
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-500 animate-pulse">Loading hospitals...</p>
              </div>
            ) : pagedRows.length === 0 ? (
              <div className="p-12 text-center">
                <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg font-medium">No hospitals found</p>
                <p className="text-gray-400 text-sm mt-1">
                  {hasActiveFilters ? "No matching hospitals found." : "Hospitals will appear here once onboarded"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Hospital
                      </th>
                      <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Branding
                      </th>
                      <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Last Updated
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {pagedRows.map((r) => {
                      const inactive = r.hospital.status !== "ACTIVE";
                      return (
                        <tr
                          key={r.hospital.id}
                          onClick={
                            !inactive
                              ? () => setEditingHospitalId(r.hospital.id)
                              : undefined
                          }
                          className={`group transition-colors duration-150 ${
                            !inactive
                              ? "cursor-pointer hover:bg-gray-50"
                              : "opacity-70 cursor-not-allowed bg-gray-50/30"
                          }`}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-lg overflow-hidden bg-primary-50 flex items-center justify-center text-primary-600 shrink-0">
                                {r.branding?.logoUrl ? (
                                  <img
                                    src={r.branding.logoUrl}
                                    alt={`${r.hospital.hospitalName} logo`}
                                    className="h-full w-full object-contain p-1"
                                  />
                                ) : (
                                  <Building2 size={18} />
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900 group-hover:text-primary-700 transition-colors">
                                  {r.hospital.hospitalName}
                                </p>
                                <p className="text-xs text-gray-400">{r.hospital.domainName}</p>
                              </div>
                            </div>
                          </td>
                          <td className="hidden sm:table-cell px-6 py-4">
                            <BrandingBadge branding={r.branding} inactive={inactive} />
                          </td>
                          <td className="hidden md:table-cell px-6 py-4">
                            {r.branding?.updatedAt ? (
                              <span className="text-sm text-gray-600">{formatDate(r.branding.updatedAt)}</span>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            {!inactive ? (
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingHospitalId(r.hospital.id);
                                  }}
                                  title={r.branding ? "Edit branding" : "Set up branding"}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-all"
                                >
                                  {r.branding ? <Edit size={16} /> : <Plus size={16} />}
                                </button>
                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 group-hover:text-primary-600 group-hover:hover:text-primary-600 transition-all">
                                  <ChevronRight size={18} />
                                </span>
                              </div>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-400 text-xs font-medium px-2.5 py-1">
                                Inactive
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && filteredRows.length > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing{" "}
                <span className="font-medium text-gray-700">
                  {(currentPage - 1) * pageSize + 1}
                </span>
                –{" "}
                <span className="font-medium text-gray-700">
                  {Math.min(currentPage * pageSize, filteredRows.length)}
                </span>{" "}
                of <span className="font-medium text-gray-700">{filteredRows.length}</span> hospitals
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-primary-50 hover:text-primary-700 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                  title="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>
                {paginationPageWindow(currentPage, totalPages).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border text-sm font-medium transition-colors ${
                      currentPage === p
                        ? "bg-primary-600 border-primary-600 text-white cursor-default"
                        : "bg-white border-gray-200 text-gray-500 hover:bg-primary-50 hover:text-primary-700"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-primary-50 hover:text-primary-700 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                  title="Next page"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Helpers ---

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 text-sm font-medium transition-colors ${
      active
        ? "border-primary-600 text-primary-700"
        : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"
    }`}
  >
    {children}
  </button>
);

const BrandingBadge = ({ branding, inactive }: { branding: HospitalMdtBranding | null; inactive: boolean }) => {
  if (inactive) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
        Inactive hospital
      </span>
    );
  }
  return branding ? (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100 ring-1 ring-green-600/10">
      Configured
    </span>
  ) : (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-100 ring-1 ring-gray-600/5">
      Not set up
    </span>
  );
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default MDTTemplateList;